// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// V13 — seed-phrase recovery signing flow.
//
// Renders inline inside `PreGateShell` when the user picks the
// Recovery tile from the V11 chooser. Replaces the V11 placeholder.
//
// Design target (from `stoic-severing-atlas.md`): survive
// `wrap.cobault.io` being unreachable without widening the verifier
// origin's attack surface. The key-agent package is stateless
// (VD11); this UI enters the mnemonic, passes it into the in-memory
// signer, derives + signs for one vault slot, and clears the
// mnemonic from React state as soon as the submit completes.
//
// V14 — derivation-index source:
//   The verifier now stores `user_derivation_index` alongside the
//   contract at vault creation (V14 protocol change). The parent
//   `VaultLookup` pulls it out of the existence-lookup response and
//   passes it down as the `derivationIndex` prop. No scan needed —
//   one nonce, one signature, one /prove call.
//

import { useMemo, useState } from 'preact/hooks';
import { api, type ProveResponse } from '../api.js';

type FlowState =
  | { kind: 'idle' }
  | { kind: 'validating' }
  | { kind: 'signing' }
  | { kind: 'error'; message: string }
  | { kind: 'signed' };

export function RecoverySignFlow({
  vaultId,
  derivationIndex,
  onSessionEstablished,
  onCancel,
}: {
  vaultId: string;
  derivationIndex: number;
  onSessionEstablished: (session: ProveResponse) => void;
  onCancel: () => void;
}) {
  const [mnemonic, setMnemonic] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [state, setState] = useState<FlowState>({ kind: 'idle' });

  const normalised = useMemo(
    () => mnemonic.trim().toLowerCase().replace(/\s+/g, ' '),
    [mnemonic],
  );
  const wordCount = useMemo(
    () => (normalised ? normalised.split(' ').length : 0),
    [normalised],
  );

  // Cheap syntactic validity — real BIP-39 checksum validation is
  // deferred to submit time so the key-agent bundle isn't loaded on
  // every keystroke.
  const lengthLooksValid = wordCount === 12 || wordCount === 24;
  const lengthFeedback =
    wordCount === 0
      ? ''
      : lengthLooksValid
        ? `${wordCount} words`
        : `${wordCount} words — mnemonic must be exactly 12 or 24 words`;

  const canSubmit =
    agreed &&
    lengthLooksValid &&
    (state.kind === 'idle' || state.kind === 'error');

  async function handleSubmit() {
    if (!canSubmit) return;

    // Capture the normalised form locally so we can clear the UI
    // state as soon as the submit starts; the local copy is out of
    // scope once this async function returns.
    const candidate = normalised;
    setState({ kind: 'validating' });
    setMnemonic('');

    // Lazy-load the key-agent so everyone else pays zero bundle cost.
    // Vite code-splits this into its own chunk.
    const { isValidMnemonic, signOwnershipInMemory } = await import(
      '@cobault/verifier-key-agent'
    );

    if (!isValidMnemonic(candidate)) {
      setState({
        kind: 'error',
        message:
          'Invalid mnemonic — check your spelling and word count (12 or 24 words).',
      });
      return;
    }

    setState({ kind: 'signing' });

    const challenge = await api.issueChallenge(vaultId);
    if (!challenge) {
      setState({
        kind: 'error',
        message: 'Could not start ownership challenge. Try again in a minute.',
      });
      return;
    }

    let sigHex: string;
    try {
      sigHex = signOwnershipInMemory(
        candidate,
        vaultId,
        challenge.nonce_hex,
        derivationIndex,
      );
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Signing failed.',
      });
      return;
    }

    const result = await api.prove(vaultId, challenge.nonce_hex, sigHex);
    if (result.kind === 'ok') {
      setState({ kind: 'signed' });
      onSessionEstablished(result.value);
      return;
    }

    let message: string;
    switch (result.kind) {
      case 'invalid-signature':
        // Per V14 the verifier hands back the correct index, so a
        // signature mismatch here means the seed doesn't control
        // this vault (rather than "try the next slot").
        message =
          "That seed doesn't control this vault. Check you're recovering from the right wallet.";
        break;
      case 'rate-limited':
        message =
          'Too many attempts in a short window. Wait a minute and try again.';
        break;
      case 'expired-or-used-nonce':
        message =
          'The ownership challenge expired before we finished. Please try again.';
        break;
      default:
        message = result.message ?? 'Unknown error while proving ownership.';
    }
    setState({ kind: 'error', message });
  }

  if (state.kind === 'signed') {
    return (
      <div class="recovery-card recovery-card--success">
        <h3 class="recovery-title">Ownership verified</h3>
        <p>Loading vault details…</p>
      </div>
    );
  }

  const busy = state.kind === 'validating' || state.kind === 'signing';

  return (
    <div class="recovery-card">
      <h3 class="recovery-title">
        Recovery path — for when the Cobault app is unavailable
      </h3>

      <div class="recovery-warning" role="alert">
        <strong>Cobault will never ask for your seed to sign a routine
          action.</strong> This is an emergency recovery flow. Your
        seed phrase is used once on this page to produce a signature,
        then cleared from memory. It is never sent to any server —
        only the derived signature leaves this page.
      </div>

      <label class="recovery-field">
        <span class="recovery-field-label">Seed phrase (12 or 24 words)</span>
        <textarea
          class="recovery-mnemonic-input"
          value={mnemonic}
          onInput={(e) => setMnemonic((e.target as HTMLTextAreaElement).value)}
          placeholder="word1 word2 word3 …"
          rows={4}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellcheck={false}
          disabled={busy}
          data-testid="recovery-mnemonic"
        />
        {lengthFeedback && (
          <span
            class={`recovery-field-help ${
              lengthLooksValid ? '' : 'recovery-field-help--error'
            }`}
          >
            {lengthFeedback}
          </span>
        )}
      </label>

      <label class="recovery-agree">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed((e.target as HTMLInputElement).checked)}
          disabled={busy}
          data-testid="recovery-agree"
        />
        <span>I understand this is a recovery flow.</span>
      </label>

      {state.kind === 'signing' && (
        <div class="recovery-progress">Signing and proving ownership…</div>
      )}

      {state.kind === 'error' && (
        <div class="recovery-error" role="alert">
          {state.message}
        </div>
      )}

      <div class="recovery-actions">
        <button
          type="button"
          class="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit || busy}
          data-testid="recovery-sign"
        >
          {busy ? 'Signing…' : 'Sign with seed phrase'}
        </button>
      </div>

      <footer class="recovery-footer">
        <span>Verifier build:</span>{' '}
        <a
          href="https://gitlab.com/chaincapital/cobault-verifier/-/commits/main"
          target="_blank"
          rel="noopener"
        >
          gitlab commits
        </a>{' '}
        — confirm in the footer at the bottom of this page that the
        commit you see matches an audited Cobault release.
      </footer>
    </div>
  );
}
